import { IGraphqlResponse } from 'types/graphql_response';
import { TaffiSubjects } from 'types/subjects';

export interface AlgoliaReactionResponse {
    hits: AlgoliaReaction[];
    nbHits: number;
    page: number;
    nbPages: number;
    hitsPerPage: number;
    exhaustiveNbHits: boolean;
    exhaustiveTypo: boolean;
    query: string;
    params: string;
    index: string;
    renderingContent: RenderingContent;
    processingTimeMS: number;
}

export enum ReactionType {
    Like = 'LIKE',
    Dislike = 'DISLIKE',
    Approved = 'APPROVED',
    Rejected = 'REJECTED',
}

export enum ReactionDocType {
    STYLE = 'styles',
    ITEMS = 'items',
}

export interface AlgoliaReaction {
    id: string;
    docId: string;
    type: ReactionType;
    creator: string;
    created: string;
    updater: null;
    updated: null;
    contextId: string;
    contextType?: 'style' | 'item';
    user: HitUser;
    doc: {
        style: HitStyle;
        item: any;
    };
    style: any;
    collectionName: string;
    extendsProps: {
        styleData: HitStyle;
        itemData: any;
        styleContext: HitStyle;
    };
    objectID: string;
    _highlightResult: HighlightResult;
    sliderIndex?: number;
}

export interface HighlightResult {
    style: HighlightResultStyle;
}

export interface HighlightResultStyle {
    user: PurpleUser;
    taffiId: Description;
    title: Description;
    description: Description;
    categories: PurpleCategory[];
    items: PurpleItem[];
    genders: StyleAge[];
    purposes: StyleAge[];
    ages: StyleAge[];
    seasons: StyleAge[];
    personas: StyleAge[];
    price: Description;
}

export interface StyleAge {
    title: Description;
}

export interface Description {
    value: string;
    matchLevel: MatchLevel;
    matchedWords: any[];
}

export enum MatchLevel {
    None = 'none',
}

export interface PurpleCategory {
    name: Description;
}

export interface PurpleItem {
    data: PurpleData;
}

export interface PurpleData {
    title: Description;
    description: Description;
}

export interface PurpleUser {
    firstName: Description;
    lastName: Description;
}

export interface HitStyle {
    id: string;
    collection: string;
    creator: IDEnum;
    created: string;
    updater: null;
    updated: null;
    user: FluffyUser;
    taffiId: string;
    title: string;
    description: string;
    images: string[];
    categories: TagElement[];
    tags: any[];
    items: FluffyItem[];
    genders: ColorElement[];
    purposes: ColorElement[];
    ages: ColorElement[];
    seasons: ColorElement[];
    personas: ColorElement[];
    bodyShapes: null;
    bodyTypes: null;
    currency: Currency;
    price: number;
    status: Status;
    imageJson: null;
    sliderIndex?: number;
    isLiked: boolean;
}

export interface ColorElement {
    id: string;
    subject: TaffiSubjects;
    title: string;
    value: string;
    type: Type;
    imageUrl: null;
    default: null;
    enabled: boolean;
    creator: AgeCreator;
    created: string;
    updater: null;
    updated: null;
}

export enum AgeCreator {
    NM3X76BT9HgWXU9KQfz9TyYckZka = 'nM3X76bT9hgWXU9KQfz9TyYckZka',
}

export enum Type {
    String = 'STRING',
}

export interface TagElement {
    id: ID;
    creator: AgeCreator;
    created: string;
    updater: null;
    updated: null;
    name: Name;
}

export enum ID {
    BxR63F1Dk5RyVS5RQBru = 'bxR63f1Dk5ryVS5RQBru',
    Cwqzvf2CcxbbJD9CGT9I = 'Cwqzvf2CcxbbJD9CgT9i',
    DgECM7FgJGGozl4Kt7U0 = 'DgECM7FgJGGozl4kt7U0',
    FvFUmh8Nn2TWZ990SaSa = 'fvFUmh8nn2TWZ990saSa',
    HubV4CGTKNOkC0InYCXm = 'hubV4cgTKNOkC0inYCXm',
    JSXrfo1UvZpHKVRmBVD0 = 'jSXrfo1uvZpHKVRmBVD0',
    Q3IjNjmr37EYwYymXlM9 = 'q3ijNjmr37eYwYymXlM9',
}

export enum Name {
    Accessories = 'Accessories',
    Classy = 'Classy',
    Dresses = 'Dresses',
    Feminine = 'Feminine',
    Footwear = 'Footwear',
    Minimalist = 'Minimalist',
    Vintage = 'Vintage',
}

export enum IDEnum {
    CBWHfksVs11UazgOwmHRFSuqMfm8 = 'CBwHfksVs11uazgOwmHRfSuqMfm8',
}

export enum Currency {
    Sar = 'SAR',
}

export interface FluffyItem {
    id: string;
    data: FluffyData;
    variant: null;
}

export interface FluffyData {
    id: string;
    collection: Collection;
    type: null;
    taffiId: string;
    price: number;
    currency: Currency;
    available: null;
    title: string;
    description: string;
    categories: TagElement[];
    tags: TagElement[];
    url: string;
    images: string[];
    maxNumOfStyles: null;
    maxNumOfItems: null;
    sizes: ColorElement[];
    colors: ColorElement[];
    creator: IDEnum;
    created: string;
    updater: null;
    updated: null;
    status: Status;
    isLiked?: boolean;
}

export enum Collection {
    Items = 'items',
    Styles = 'styles',
}

export enum Status {
    Available = 'available',
    Published = 'published',
    Unlisted = 'unlisted',
    Unpublished = 'unpublished',
    Draft = 'draft',
    Private = 'private',
}

export interface FluffyUser {
    id: IDEnum;
    creator: IDEnum;
    created: string;
    updater: null;
    updated: null;
    phone: string;
    firstName: string;
    lastName: string;
    userName: string;
    email: string;
    gender: null;
    userType: string;
    approved: boolean;
}

export interface HitUser {
    email: string;
    id: string;
}

export interface RenderingContent {}

export type ReactionAlgoliaQuery = IGraphqlResponse<AlgoliaReactionResponse, 'reactions'>;
